import { inject, NgZone } from '@angular/core';
import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import { AgentSessionStateService } from 'AgentApp/ts/services/agent-session-state.service';
import { UrlsService } from 'NewAngular/services/core/urls.service';
import { AjaxService } from 'NewAngular/services/core/ajax.service';
import { firstValueFrom } from 'rxjs';
import * as i0 from "@angular/core";
const MAX_FILE_SIZE_IN_BYTES = 33554433; // 32MB
const SSE_EVENT_TYPES = [
    'text_delta',
    'thinking_delta',
    'tool_call_start',
    'tool_call_stop',
    'done',
    'error',
    'error_max_sessions',
    'requesting_confirmation',
    'interrupted',
    'interaction_id',
];
export class AgentTransportService {
    constructor() {
        this._ngZone = inject(NgZone);
        this._urlsService = inject(UrlsService);
        this._ajaxService = inject(AjaxService);
        this._sessionState = inject(AgentSessionStateService);
        this._eventSources = new Map();
        this._interactionIds = new Map();
        this._pendingConfirmations = new Map();
        this._isFileUploading = false;
        this._uppy = new Uppy({
            autoProceed: false,
            restrictions: {
                maxFileSize: MAX_FILE_SIZE_IN_BYTES,
                maxNumberOfFiles: 5,
                allowedFileTypes: ['image/*', '.pdf', '.xlsx', '.csv', '.txt'],
            },
        });
        this._uppy.use(XHRUpload, {
            endpoint: this._urlsService.getUrl(this._urlsService.AI_CHAT.AGENT_FILE_UPLOAD, {
                provider: 'anthropic',
            }),
            formData: true,
            fieldName: 'files',
            bundle: true,
        });
    }
    get isFileUploading() {
        return this._isFileUploading;
    }
    async createSession(message) {
        const url = this._urlsService.getUrl(this._urlsService.AGENT.SESSIONS);
        const response = await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, {
            provider: 'anthropic',
            message,
        }));
        return { id: response.agent_session_id, name: response.name, icon: response.icon };
    }
    async getActiveSessions(userId) {
        const url = `${this._getBaseUrl()}/agent/active_sessions?user_id=${userId}`;
        try {
            const response = await fetch(url, { credentials: 'include' });
            const data = await response.json();
            return data.active_sessions ?? {};
        }
        catch {
            return {};
        }
    }
    hasActiveConnection(sessionId) {
        const es = this._eventSources.get(sessionId);
        return !!es && es.readyState !== EventSource.CLOSED;
    }
    setInteractionId(sessionId, interactionId) {
        this._interactionIds.set(sessionId, interactionId);
    }
    reconnectToSession(sessionId, userId, interactionId, lastMessageStreamId) {
        this._closeEventSource(sessionId);
        this._sessionState.markReconnecting(sessionId);
        const params = new URLSearchParams({
            agent_session_id: sessionId.toString(),
            user_id: userId.toString(),
            interaction_id: interactionId,
        });
        if (lastMessageStreamId) {
            params.set('last_message_stream_id', lastMessageStreamId);
        }
        const sseUrl = `${this._getBaseUrl()}/agent/new?${params.toString()}`;
        this._openEventSource(sessionId, sseUrl, true);
    }
    async sendUserMessage(sessionId, text, files = []) {
        const messageFiles = files.map((file) => ({
            attachment_url: file.name,
            attachment_mime_type: file.type,
            external_file_id: '',
        }));
        this._sessionState.addUserMessage(sessionId, text, messageFiles);
        let fileContentBlocks = [];
        if (files.length) {
            this._isFileUploading = true;
            files.forEach((file) => {
                this._uppy.addFile({ name: file.name, type: file.type, data: file });
            });
            try {
                const result = await this._uppy.upload();
                fileContentBlocks = (result?.successful?.[0]?.response?.body?.files ?? []);
            }
            finally {
                this._uppy.cancelAll();
                this._isFileUploading = false;
            }
        }
        const messageData = [
            ...fileContentBlocks.map((f) => ({ type: f.type, file_id: f.file_id })),
            { type: 'text', text },
        ];
        const encodedMessage = JSON.stringify({ type: 'user_input', data: messageData });
        this._sessionState.markStreaming(sessionId);
        const interactionId = this._interactionIds.get(sessionId);
        const params = new URLSearchParams({
            agent_session_id: sessionId.toString(),
            message: encodedMessage,
            user_id: window.USER.userId.toString(),
        });
        if (interactionId) {
            params.set('interaction_id', interactionId);
        }
        const sseUrl = `${this._getBaseUrl()}/agent/new?${params.toString()}`;
        // Close any existing stream for this session
        this._closeEventSource(sessionId);
        this._openEventSource(sessionId, sseUrl);
    }
    async interrupt(sessionId) {
        const interactionId = this._interactionIds.get(sessionId);
        if (!interactionId) {
            return;
        }
        this._closeEventSource(sessionId);
        try {
            const url = `${this._getBaseUrl()}/agent/stop?agent_session_id=${sessionId}&interaction_id=${interactionId}`;
            await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, {}, false, true));
        }
        catch {
            // Ignore errors
        }
        this._sessionState.handleEvent(sessionId, { type: 'interrupted' });
    }
    disconnect(sessionId) {
        this._closeEventSource(sessionId);
        this._sessionState.markDisconnected(sessionId);
    }
    confirmToolCall(sessionId, userId, authorize, alwaysApply, toolCall) {
        let answer;
        if (authorize) {
            answer = alwaysApply ? 'allow_always' : 'allow';
        }
        else {
            answer = 'reject';
        }
        const confirmation = { tool_use_id: toolCall.tool_use_id, answer, reason: null };
        if (!this._pendingConfirmations.has(sessionId)) {
            this._pendingConfirmations.set(sessionId, []);
        }
        this._pendingConfirmations.get(sessionId).push(confirmation);
        this._sessionState.removeToolCallConfirmation(sessionId, toolCall.tool_use_id);
        const remaining = this._sessionState.getChatState(sessionId).toolCallsToConfirm;
        if (remaining.length === 0) {
            this._sendConfirmations(sessionId, userId);
        }
    }
    _openEventSource(sessionId, url, isReconnection = false) {
        const eventSource = new EventSource(url, { withCredentials: true });
        let receivedEvent = false;
        let reconnectionTimeout = null;
        if (isReconnection) {
            reconnectionTimeout = setTimeout(() => {
                if (!receivedEvent) {
                    this._closeEventSource(sessionId);
                    this._sessionState.handleEvent(sessionId, { type: 'done', data: [] });
                }
            }, 5000);
        }
        SSE_EVENT_TYPES.forEach((eventType) => {
            eventSource.addEventListener(eventType, (e) => {
                this._ngZone.run(() => {
                    // Don't count interaction_id as a "real" event for timeout purposes —
                    // it's always emitted first, before any stream content arrives
                    if (eventType !== 'interaction_id') {
                        receivedEvent = true;
                        if (reconnectionTimeout) {
                            clearTimeout(reconnectionTimeout);
                            reconnectionTimeout = null;
                        }
                    }
                    this._handleSSEEvent(sessionId, eventType, e.data);
                });
            });
        });
        eventSource.onerror = () => {
            if (isReconnection && !receivedEvent) {
                // During reconnection, suppress immediate error — let timeout handle it
                return;
            }
            this._closeEventSource(sessionId);
            if (reconnectionTimeout) {
                clearTimeout(reconnectionTimeout);
            }
            this._sessionState.handleEvent(sessionId, { type: 'error', data: 'Connection lost. Please try again.' });
        };
        this._eventSources.set(sessionId, eventSource);
    }
    _closeEventSource(sessionId) {
        const existing = this._eventSources.get(sessionId);
        if (existing) {
            existing.close();
            this._eventSources.delete(sessionId);
        }
    }
    _handleSSEEvent(sessionId, eventType, data) {
        if (eventType === 'interaction_id') {
            this._interactionIds.set(sessionId, data);
            return;
        }
        if (eventType === 'done') {
            this._closeEventSource(sessionId);
        }
        if (eventType === 'error_max_sessions') {
            this._closeEventSource(sessionId);
            this._sessionState.handleEvent(sessionId, {
                type: 'error',
                data: 'You have too many active sessions. Please wait for one to finish before starting a new one.',
            });
            return;
        }
        try {
            const parsed = JSON.parse(data);
            this._sessionState.handleEvent(sessionId, parsed);
        }
        catch {
            // Ignore non-JSON event
        }
    }
    async _sendConfirmations(sessionId, userId) {
        const interactionId = this._interactionIds.get(sessionId);
        const confirmations = this._pendingConfirmations.get(sessionId) ?? [];
        this._pendingConfirmations.delete(sessionId);
        if (!interactionId || !confirmations.length) {
            return;
        }
        try {
            const params = new URLSearchParams({
                agent_session_id: sessionId.toString(),
                interaction_id: interactionId,
                user_id: userId.toString(),
            });
            const url = `${this._getBaseUrl()}/agent/confirm?${params.toString()}`;
            await firstValueFrom(this._ajaxService.postJsonDataObserveBody(url, { confirmations }, false, true));
            // Reconnect to the SSE stream to receive subsequent events
            if (!this.hasActiveConnection(sessionId)) {
                const lastStreamId = this._sessionState.getLastMessageStreamId(sessionId);
                this.reconnectToSession(sessionId, userId, interactionId, lastStreamId);
            }
        }
        catch {
            this._sessionState.handleEvent(sessionId, {
                type: 'error',
                data: 'Failed to send tool confirmation. Please try again.',
            });
        }
    }
    _getBaseUrl() {
        return window.location.hostname === 'localhost'
            ? `${window.location.protocol}//localhost:3003`
            : process.env.AI_PROXY_BASE_URL;
    }
}
AgentTransportService.ɵfac = function AgentTransportService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AgentTransportService)(); };
AgentTransportService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AgentTransportService, factory: AgentTransportService.ɵfac, providedIn: 'root' });
